/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { sigintCreateSignal } from "../funcs/sigintCreateSignal.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SigintSignal } from "../models/components/sigintsignal.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  CreateSigintSignalRequest,
  CreateSigintSignalSecurity,
} from "../models/operations/createsigintsignal.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type CreateSigintSignalMutationVariables = {
  request: CreateSigintSignalRequest;
  security?: CreateSigintSignalSecurity | undefined;
  options?: RequestOptions;
};

export type CreateSigintSignalMutationData = SigintSignal;

export type CreateSigintSignalMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * createSignal sigint
 *
 * @remarks
 * Create a reusable custom signal. Names are trimmed and must contain 1 to 200 characters. Description and classifier criteria are optional authoring metadata; an empty string clears either field.
 */
export function useCreateSigintSignalMutation(
  options?: MutationHookOptions<
    CreateSigintSignalMutationData,
    CreateSigintSignalMutationError,
    CreateSigintSignalMutationVariables
  >,
): UseMutationResult<
  CreateSigintSignalMutationData,
  CreateSigintSignalMutationError,
  CreateSigintSignalMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildCreateSigintSignalMutation(client, options),
    ...options,
  });
}

export function mutationKeyCreateSigintSignal(): MutationKey {
  return ["@gram/client", "sigint", "createSignal"];
}

export function buildCreateSigintSignalMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: CreateSigintSignalMutationVariables,
  ) => Promise<CreateSigintSignalMutationData>;
} {
  return {
    mutationKey: mutationKeyCreateSigintSignal(),
    mutationFn: function createSigintSignalMutationFn({
      request,
      security,
      options,
    }): Promise<CreateSigintSignalMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(sigintCreateSignal(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
